/**
 * The opportunity matrix's two axes. Each dimension feeds Impact,
 * Feasibility or neither (Configuration → Business Opportunities); an idea's position on
 * an axis is the weighted average of its scores on that axis's dimensions,
 * with the dimensions' own weights. An axis whose weights are all 0 averages
 * its dimensions evenly.
 */

import type { Axis, Dimension, IdeaPoint, Industry } from '@/api/types';

/** What placing an idea needs; list rows and matrix points both have it. */
export type MatrixIdea = Pick<IdeaPoint, 'id' | 'title' | 'industry' | 'verdict' | 'total' | 'scores'>;

export function axisDimensions(dimensions: Dimension[], axis: Axis): Dimension[] {
  return dimensions.filter((d) => d.axis === axis);
}

export function axisScore(scores: Record<string, number | null>, dimensions: Dimension[]): number | null {
  const scored = dimensions.filter((d) => typeof scores[d.key] === 'number');
  if (scored.length === 0) return null;
  const weight = scored.reduce((sum, d) => sum + d.weight, 0);
  const total =
    weight > 0
      ? scored.reduce((sum, d) => sum + (scores[d.key] as number) * d.weight, 0) / weight
      : scored.reduce((sum, d) => sum + (scores[d.key] as number), 0) / scored.length;
  return Math.round(total * 10) / 10;
}

export interface MatrixPoint {
  idea: MatrixIdea;
  impact: number;
  feasibility: number;
}

/** Ideas with a score on both axes; the rest cannot be placed yet. */
export function matrixPoints(ideas: MatrixIdea[], dimensions: Dimension[]): { points: MatrixPoint[]; unplaced: number } {
  const impact = axisDimensions(dimensions, 'impact');
  const feasibility = axisDimensions(dimensions, 'feasibility');
  const points: MatrixPoint[] = [];
  for (const idea of ideas) {
    const x = axisScore(idea.scores, impact);
    const y = axisScore(idea.scores, feasibility);
    if (x !== null && y !== null) points.push({ idea, impact: x, feasibility: y });
  }
  return { points, unplaced: ideas.length - points.length };
}

/** A scatter tells at most this many hues apart for every reader; the rest share "Other". */
export const INDUSTRY_SLOTS = 3;

/**
 * The industries the matrix colours, slot by slot: the ones with the most
 * ideas overall (ties by name). Taken from every idea, not the filtered ones,
 * so a filter never repaints an industry.
 */
export function industrySlots(industries: Pick<Industry, 'id' | 'name' | 'idea_count'>[]): Map<string, number> {
  const ranked = industries
    .filter((i) => i.idea_count > 0)
    .sort((a, b) => b.idea_count - a.idea_count || a.name.localeCompare(b.name))
    .slice(0, INDUSTRY_SLOTS);
  return new Map(ranked.map((industry, index) => [industry.id, index + 1]));
}
